import { faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { parse } from "query-string";
import React from "react";
import Helmet from "react-helmet";
import { MapStateToPropsParam } from "react-redux";

import LeftMenu from "@/mk2/apps/blogs/components/LeftMenu";
import { forumReadFeedTrigger } from "@/mk2/apps/forum/actions";
import { OTHER_CATEGORY_SLUG, UNCATEGORIZED_TOPIC_SLUG } from "@/mk2/apps/forum/constants";
import ForumFeed from "@/mk2/apps/forum/containers/ForumFeed/ForumFeed";
import { forumFeedLoad } from "@/mk2/apps/forum/containers/ForumFeed/ForumFeed.actions";
import forumFeedPageReducer, {
    ForumFeedInfiniteListState,
    ForumFeedPageState,
    ORDER_BY_NEWEST,
    ORDER_BY_UPDATED,
    OrderBy,
} from "@/mk2/apps/forum/containers/ForumFeed/ForumFeed.reducers";
import forumFeedSaga from "@/mk2/apps/forum/containers/ForumFeed/ForumFeed.sagas";
import {
    getForumFeedInfiniteListState,
    getForumFeedPageState,
} from "@/mk2/apps/forum/containers/ForumFeed/ForumFeed.selectors";
import {
    getForumComplaintsStatistics,
    getForumNewestCount,
    getForumNotificationsOn,
    getForumSubscribedCount,
} from "@/mk2/apps/forum/reducers";
import {
    BrandDaysCampaignEntity,
    BrandDaysCampaignSchema,
    ComplaintsStatisticsPerPost,
    PostEntity,
    PostSchema,
} from "@/mk2/apps/forum/schemas";
import { breadcrumbsForum } from "@/mk2/apps/forum/structuredData";
import { forumPostNewInTopicUrl, forumUpdatedUrl, forumUrl } from "@/mk2/apps/forum/urls";
import { groupsSubscribedFeedUrl } from "@/mk2/apps/groups/urls";
import { BrandDaysPosts } from "@/mk2/apps/home/components/BrandDaysPosts";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { MenuSelection, UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    ADD_FEED_POST,
    SEO_DESCRIPTION_FORUM,
    SEO_TITLE_FORUM,
    TAB_FORUMS,
    TAB_GROUPS,
} from "@/mk/autogenerated/translations/ForumFeedPage.6c6239b92b6cf102841826f4920a74d6";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import { FORUM_FEED_MENU_ORDERBY } from "./ForumFeed.constants";

const fasPlusCircleLg: FontAwesomeIconProps = { icon: faPlusCircle, size: "lg" };

interface OwnProps {}

interface StateProps {
    after: number;
    posts: PostEntity[];
    hasNext: number;
    hasPrevious: number;
    isMobile: boolean;
    notificationsOn: number[];
    requestUser: UserEntity;
    loadingState: LoadingState;
    errorMessage: string;
    baseUrl: string;
    unreadCount: number;
    permissions: string[];
    complaintsStatistics: ComplaintsStatisticsPerPost;
    subscribedCount: number;
    orderBy: OrderBy;
    activeBrandDaysCampaign: BrandDaysCampaignEntity;
    activeBrandDaysPosts: PostEntity[];
    topicsCount: number;
}

interface DispatchProps {
    onForumFeedMenuOrderBy();
    onLoad(orderBy: OrderBy, after?: number);
    onRead();
}

type RouteProps = PageRouteProps<{}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ForumFeedPage extends Page<OwnProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const after = parsed.after as number;

            const orderBy: OrderBy =
                ownProps.routePathPattern === forumUpdatedUrl ? ORDER_BY_UPDATED : ORDER_BY_NEWEST;

            const pageState: ForumFeedPageState = getForumFeedPageState(state);
            const feedState: ForumFeedInfiniteListState = getForumFeedInfiniteListState(
                state,
                orderBy,
            );

            return {
                after,
                baseUrl: getRequestBaseUrl(state),
                complaintsStatistics: getForumComplaintsStatistics(state),
                errorMessage: feedState.errorMessage,
                hasNext: feedState.hasNext,
                hasPrevious: feedState.hasPrevious,
                loadingState: feedState.loadingState,
                isMobile: getRequestDeviceMobile(state),
                notificationsOn: getForumNotificationsOn(state),
                orderBy,
                permissions: getRequestPermissions(state),
                posts: getDenormalizedEntities<PostEntity>(state, PostSchema, feedState.ids),
                requestUser: getRequestUser(state),
                topicsCount: pageState.topicsCount,
                subscribedCount: getForumSubscribedCount(state),
                unreadCount: getForumNewestCount(state),
                activeBrandDaysCampaign: getDenormalizedEntity<BrandDaysCampaignEntity>(
                    state,
                    BrandDaysCampaignSchema,
                    pageState.activeBrandDaysCampaignId,
                ),
                activeBrandDaysPosts: getDenormalizedEntities<PostEntity>(
                    state,
                    PostSchema,
                    pageState.activeBrandDaysPostsIds,
                ),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onForumFeedMenuOrderBy: () => menuToggle(FORUM_FEED_MENU_ORDERBY),
            onLoad: forumFeedLoad,
            onRead: () => forumReadFeedTrigger("updated"),
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_FORUM;
    }

    public getMetaDescription(props: Props): string {
        return SEO_DESCRIPTION_FORUM;
    }

    public getStructuredData(props: Props): object[] {
        return [breadcrumbsForum(props.baseUrl)];
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return props.orderBy === ORDER_BY_UPDATED ? baseUrl + forumUpdatedUrl : baseUrl + forumUrl;
    }

    public render(props: Props): false | JSX.Element {
        const rssLink = props.orderBy === ORDER_BY_UPDATED ? forumUpdatedUrl : forumUrl;
        return (
            <span>
                <Helmet>
                    <link
                        rel="alternate"
                        type="application/atom+xml"
                        href={`${props.baseUrl}/rss${rssLink}`}
                        title={SEO_TITLE_FORUM}
                    />
                </Helmet>
                <ForumFeed {...props} />
            </span>
        );
    }

    public renderRight(props: Props): JSX.Element {
        const { activeBrandDaysCampaign, activeBrandDaysPosts, location, requestUser } = props;

        const showBrandDays =
            !!activeBrandDaysCampaign && activeBrandDaysPosts.length > 0 && hasAds(requestUser);
        return (
            <>
                {showBrandDays && (
                    <BrandDaysPosts
                        brandDaysCampaign={activeBrandDaysCampaign}
                        brandDaysPosts={activeBrandDaysPosts}
                    />
                )}
                <LeftMenu location={location} />
            </>
        );
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Forum Category - Mid 01 - Desktop",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile
                ? "Forum Category - Top 01 - Mobile"
                : "Forum Category - Top 01 - Desktop",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum Category - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, posts } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((posts?.length || 0) < 10) {
            return null;
        }

        return {
            zone: "Forum Category - Anchor - Mobile",
        };
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        return [
            <Btn
                key="forums"
                type={BtnType.Tab}
                label={TAB_FORUMS}
                link={forumUpdatedUrl}
                active
            />,
            <Btn
                key="groups"
                type={BtnType.Tab}
                label={TAB_GROUPS}
                link={groupsSubscribedFeedUrl}
            />,
        ];
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        return (
            <Btn
                type={BtnType.Blue}
                label={ADD_FEED_POST}
                icon={fasPlusCircleLg}
                link={url(forumPostNewInTopicUrl, {
                    categorySlug: OTHER_CATEGORY_SLUG,
                    topicSlug: UNCATEGORIZED_TOPIC_SLUG,
                })}
            />
        );
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "forum",
            activeItem: "feed",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.forumFeed", forumFeedPageReducer);
        store.injectSaga("containers.forum.forumFeed", forumFeedSaga);
    }
}
